import { useEffect, useRef, type RefObject } from 'react';
import mapboxgl from 'mapbox-gl';
import type { AreaSelect } from './MapTypes';
import type { Pin } from './mapPins/MapPinTypes';

// A drag shorter than this is a stray click, not a rectangle.
const MIN_AREA_PX = 4;

const useAreaSelect = <T>(
	mapRef: RefObject<mapboxgl.Map | null>,
	inited: boolean,
	areaSelect: AreaSelect<T> | undefined,
	pins: Pin<T>[],
	boxClassName: string
) => {
	const listener = useRef(areaSelect);
	const currentPins = useRef(pins);
	useEffect(() => {
		listener.current = areaSelect;
		currentPins.current = pins;
	}, [areaSelect, pins]);
	const active = !!areaSelect?.active;

	useEffect(() => {
		const map = mapRef.current;
		if (!inited || !map || !active) {
			return;
		}
		const container = map.getContainer();
		const canvas = map.getCanvasContainer();
		const point = (event: PointerEvent) => {
			const rect = container.getBoundingClientRect();
			return new mapboxgl.Point(event.clientX - rect.left, event.clientY - rect.top);
		};
		let start: mapboxgl.Point | null = null;
		let box: HTMLDivElement | null = null;

		const move = (event: PointerEvent) => {
			if (!start) {
				return;
			}
			const to = point(event);
			if (!box) {
				box = document.createElement('div');
				box.className = boxClassName;
				container.appendChild(box);
			}
			box.style.left = `${Math.min(start.x, to.x)}px`;
			box.style.top = `${Math.min(start.y, to.y)}px`;
			box.style.width = `${Math.abs(start.x - to.x)}px`;
			box.style.height = `${Math.abs(start.y - to.y)}px`;
		};
		const stopDrawing = () => {
			box?.remove();
			box = null;
			start = null;
		};
		const up = (event: PointerEvent) => {
			const from = start;
			stopDrawing();
			if (!from) {
				return;
			}
			const to = point(event);
			if (Math.abs(from.x - to.x) < MIN_AREA_PX || Math.abs(from.y - to.y) < MIN_AREA_PX) {
				return;
			}
			// Tested on screen, not as a lat/lng box: once the map is rotated or pitched the two
			// corners no longer describe the rectangle that was drawn.
			const [left, right] = [Math.min(from.x, to.x), Math.max(from.x, to.x)];
			const [top, bottom] = [Math.min(from.y, to.y), Math.max(from.y, to.y)];
			const inside = currentPins.current.filter(pin => {
				const { x, y } = map.project([pin.longitude, pin.latitude]);
				return x >= left && x <= right && y >= top && y <= bottom;
			});
			listener.current?.onSelect(inside);
		};
		const down = (event: PointerEvent) => {
			if (event.button !== 0 || event.ctrlKey) {
				return;
			}
			event.preventDefault();
			canvas.setPointerCapture(event.pointerId);
			start = point(event);
		};
		const escape = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				listener.current?.onCancel();
			}
		};

		map.dragPan.disable();
		map.dragRotate.disable();
		map.boxZoom.disable();
		// Mapbox's own `.mapboxgl-interactive { cursor: grab }` outranks a class.
		canvas.style.cursor = 'crosshair';
		canvas.addEventListener('pointerdown', down);
		canvas.addEventListener('pointermove', move);
		canvas.addEventListener('pointerup', up);
		canvas.addEventListener('pointercancel', up);
		document.addEventListener('keydown', escape);
		return () => {
			canvas.removeEventListener('pointerdown', down);
			canvas.removeEventListener('pointermove', move);
			canvas.removeEventListener('pointerup', up);
			canvas.removeEventListener('pointercancel', up);
			document.removeEventListener('keydown', escape);
			stopDrawing();
			canvas.style.cursor = '';
			map.dragPan.enable();
			map.dragRotate.enable();
			map.boxZoom.enable();
		};
	}, [mapRef, inited, active, boxClassName]);
};

export default useAreaSelect;
